// Broadcasts the teacher's notes-panel state (open/closed + current text) over
// the LiveKit DataChannel so the recording-view compositor can show it in the
// recorded MP4. Rendered as a null component inside the LiveKitRoom context.
//
// The lesson's hidden egress-bot participant subscribes to this topic on the
// recording-view page and renders the notes as an overlay when open=true.
// Students receive these packets too but ignore them (they have no handler).

import { useEffect, useRef } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';

export const NOTES_DATA_TOPIC = 'teacher-notes-state';

export default function TeacherNotesBroadcaster({ open, text }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const lastSentRef = useRef({ open: null, text: null });

  useEffect(() => {
    if (!room || !localParticipant) return;
    if (room.state !== 'connected') return;
    // Only broadcast when something actually changed — avoid spamming the data
    // channel on every re-render.
    if (lastSentRef.current.open === open && lastSentRef.current.text === text) return;
    lastSentRef.current = { open, text };

    try {
      const payload = new TextEncoder().encode(
        JSON.stringify({ open: !!open, text: String(text || '') }),
      );
      // Reliable delivery; recording-view is the only consumer, and losing a
      // "notes closed" packet would leave stale notes on the recording.
      localParticipant.publishData(payload, {
        reliable: true,
        topic: NOTES_DATA_TOPIC,
      });
    } catch (err) {
      console.warn('[notes-broadcast] publishData failed:', err);
    }
  }, [room, localParticipant, open, text]);

  return null;
}
